import { useCallback, useMemo } from 'react';

import { FARE_ATTRIBUTES } from '@web-platform/shared-universal-constants/src/queryParams';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { redirect } from 'saddlebag-browser';

import {
  mapFilterStateToSearchParam,
  mapSearchParamToFilterState,
} from '../fareAttributes/mappers';
import { areURLSearchParamsEqual } from '../queryParams/areUrlSearchParamsEqual';

import type { ValidAttributeType } from '@web-platform/shared-universal-constants/src/filterAttributes';
import type { NavigateOptions } from 'react-router-dom';

export type UseSearchParamOpts = NavigateOptions & {
  shouldRedirect?: boolean;
};

const DEFAULT_NAVIGATE_OPTS = { replace: true, shouldRedirect: false };

export const useSearchParam = ({
  key,
}: {
  key: string;
}): [string | null, (newValue: string | null, navigateOpts?: UseSearchParamOpts) => void] => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currSearchParams] = useSearchParams();

  const setSearchParamValue = useCallback(
    (newValue: string | null, navigateOpts: UseSearchParamOpts = DEFAULT_NAVIGATE_OPTS) => {
      const newSearchParams = new URLSearchParams(currSearchParams);

      if (newValue === null) {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, newValue);
      }

      if (areURLSearchParamsEqual(currSearchParams, newSearchParams)) {
        return;
      }

      const { shouldRedirect, ...navigateOptions } = navigateOpts ?? {};

      if (shouldRedirect) {
        redirect(`?${newSearchParams.toString()}`);
      } else {
        navigate({ ...location, search: newSearchParams.toString() }, navigateOptions);
      }
    },
    [currSearchParams, key, navigate, location],
  );

  return [currSearchParams.get(key), setSearchParamValue];
};

export const useFareAttributesSearchParam = () => {
  const [fareAttributesParam, setFareAttributesParam] = useSearchParam({ key: FARE_ATTRIBUTES });

  const selectedFilterState = useMemo(
    () => mapSearchParamToFilterState(fareAttributesParam),
    [fareAttributesParam],
  );

  const setFareAttribute = useCallback(
    (attributeType: ValidAttributeType, isSelected: boolean, navigateOpts?: UseSearchParamOpts) => {
      let newState: ValidAttributeType[] = [];

      if (isSelected && !selectedFilterState.includes(attributeType)) {
        newState = [...selectedFilterState, attributeType];
      }

      if (!isSelected) {
        newState = selectedFilterState.filter((attr) => attr !== attributeType);
      }

      setFareAttributesParam(mapFilterStateToSearchParam(newState), navigateOpts);
    },
    [selectedFilterState, setFareAttributesParam],
  );

  const clearAllFareAttributes = useCallback(() => {
    setFareAttributesParam(null);
  }, [setFareAttributesParam]);

  return { selectedFilterState, setFareAttribute, clearAllFareAttributes };
};
